import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Typography, Alert, Divider,
  Table, TableHead, TableRow, TableCell, TableBody, LinearProgress, Box,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { StatusBadge } from '../../components/StatusBadge';
import { workOrdersApi, type WorkOrder } from '../../api/workOrders.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const ACTIONS_BY_STATUS: Record<string, { action: string; label: string; approve?: boolean }[]> = {
  draft: [{ action: 'submit', label: 'Submit' }, { action: 'cancel', label: 'Cancel', approve: true }],
  pending_approval: [{ action: 'approve', label: 'Approve', approve: true }, { action: 'reject', label: 'Reject', approve: true }, { action: 'cancel', label: 'Cancel', approve: true }],
  approved: [{ action: 'start', label: 'Start Work' }, { action: 'cancel', label: 'Cancel', approve: true }],
  in_progress: [{ action: 'complete', label: 'Mark Completed' }, { action: 'cancel', label: 'Cancel', approve: true }],
  completed: [{ action: 'close', label: 'Close', approve: true }],
  closed: [],
  cancelled: [],
  rejected: [],
};

export function WorkOrderDetailDialog({ woId, onClose }: { woId: number | null; onClose: () => void }) {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const open = woId !== null;
  const [error, setError] = useState<string | null>(null);

  const wo = useQuery({ queryKey: ['wo', woId], queryFn: () => workOrdersApi.get(woId as number), enabled: open });
  const refresh = () => { qc.invalidateQueries({ queryKey: ['wo', woId] }); qc.invalidateQueries({ queryKey: ['work-orders'] }); };

  const statusMut = useMutation({
    mutationFn: (action: string) => workOrdersApi.changeStatus(woId as number, action),
    onSuccess: refresh,
    onError: (e) => setError(extractApiError(e)),
  });

  if (!open) return null;
  const d: WorkOrder | undefined = wo.data;
  const actions = (ACTIONS_BY_STATUS[d?.status ?? ''] ?? []).filter((a) => hasPermission(a.approve ? 'wo.approve' : 'wo.update'));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{d?.code} — {d?.title} <StatusBadge status={d?.status} /></DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <Stack direction="row" spacing={3} mb={1} flexWrap="wrap">
          <Typography variant="body2">Total: <b>₹{(d?.totalAmount ?? 0).toLocaleString()}</b></Typography>
          {d?.vendorId && <Typography variant="body2">Vendor #{d.vendorId}</Typography>}
          {d?.technicianId && <Typography variant="body2">Technician #{d.technicianId}</Typography>}
          {d?.startDate && <Typography variant="body2">Start: {d.startDate}</Typography>}
          {d?.completionDate && <Typography variant="body2">Completed: {d.completionDate}</Typography>}
        </Stack>
        {d && d.status === 'in_progress' && (
          <Box mb={2}><Typography variant="caption" color="text.secondary">Progress {d.percentComplete}%</Typography><LinearProgress variant="determinate" value={d.percentComplete} sx={{ height: 8, borderRadius: 5 }} /></Box>
        )}
        {d?.scopeOfWork && <Typography variant="body2" color="text.secondary" mb={2}>{d.scopeOfWork}</Typography>}

        {actions.length > 0 && (
          <Stack direction="row" spacing={1} mb={2} flexWrap="wrap">
            {actions.map((a) => (
              <Button key={a.action} size="small" variant="outlined" color={a.action === 'reject' || a.action === 'cancel' ? 'error' : a.action === 'approve' ? 'success' : 'primary'} onClick={() => statusMut.mutate(a.action)}>
                {a.label}
              </Button>
            ))}
          </Stack>
        )}

        <Divider sx={{ mb: 1 }} />
        <Table size="small">
          <TableHead><TableRow><TableCell>Description</TableCell><TableCell>Unit</TableCell><TableCell align="right">Qty</TableCell><TableCell align="right">Rate</TableCell><TableCell align="right">Amount</TableCell></TableRow></TableHead>
          <TableBody>
            {(d?.items ?? []).map((it) => (
              <TableRow key={it.id}>
                <TableCell>{it.description}</TableCell><TableCell>{it.unit}</TableCell>
                <TableCell align="right">{it.quantity}</TableCell><TableCell align="right">₹{it.rate.toLocaleString()}</TableCell>
                <TableCell align="right">₹{(it.amount ?? 0).toLocaleString()}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}
